import { defineStore } from 'pinia'
import { authCookies } from '@/utils/cookies'

interface SessionState {
  email: string
  token: string
  temporaryToken: string
  validationTokenSentAt: Date | null
}

const TIME_TO_RESEND_TOKEN = 5 * 60 * 1000

export const useSessionStore = defineStore('auth-session', {
  state: (): SessionState => {
    const session = authCookies.getSession()
    const tempSession = authCookies.getTemporarySession()
    
    return {
      email: session?.email || tempSession?.email || '',
      token: session?.token || '',
      temporaryToken: tempSession?.temporaryToken || '',
      validationTokenSentAt: tempSession?.validationTokenSentAt 
        ? new Date(tempSession.validationTokenSentAt) 
        : null,
    }
  },
  
  getters: {
    hasSession: (state) => !!(state.email && state.token),
    hasTemporarySession: (state) => !!(state.email && state.temporaryToken),
    
    getTimeToResendToken: (state) => {
      if (!state.validationTokenSentAt) return 0
      
      const sentAt = new Date(state.validationTokenSentAt).getTime()
      const remaining = (sentAt + TIME_TO_RESEND_TOKEN) - Date.now()
      return Math.floor(Math.max(0, remaining / 1000))
    },
  },
  
  actions: {
    createSession({ email, token }: { email: string; token: string }) {
      this.email = email
      this.token = token
      this.temporaryToken = ''
      this.validationTokenSentAt = null
      authCookies.setSession(email, token)
    },
    
    createTemporarySession({ 
      email, 
      temporaryToken, 
      validationTokenSentAt 
    }: { 
      email: string
      temporaryToken: string
      validationTokenSentAt: Date 
    }) {
      this.email = email
      this.temporaryToken = temporaryToken
      this.token = ''
      this.validationTokenSentAt = validationTokenSentAt
      authCookies.setTemporarySession(email, temporaryToken, validationTokenSentAt)
    },
    
    setValidationTokenSentAt(validationTokenSentAt: Date) {
      this.validationTokenSentAt = validationTokenSentAt
      authCookies.updateValidationTokenSentAt(validationTokenSentAt)
    },
    
    destroySession() {
      this.$reset()
      authCookies.clearAll()
    },
    
    syncFromCookies() {
      const session = authCookies.getSession()
      const tempSession = authCookies.getTemporarySession()
      
      if (session?.token) {
        this.email = session.email
        this.token = session.token
        this.temporaryToken = ''
        this.validationTokenSentAt = null
      } else if (tempSession?.temporaryToken) {
        this.email = tempSession.email
        this.temporaryToken = tempSession.temporaryToken
        this.token = ''
        this.validationTokenSentAt = tempSession.validationTokenSentAt 
          ? new Date(tempSession.validationTokenSentAt) 
          : null
      }
    },
  },
})

